import { LoadLocation } from '../load/load.model';
import { twApi } from './../api';
import { LocationSearchData, CreateLocationData, UpdateLocationData } from './location.model';

/**
 * Api endpoints for location entity
 */
const locationApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    getLocations: builder.query<{ locations: LoadLocation[]; total: number }, LocationSearchData>({
      query: (arg) => ({
        url: `/locations`,
        params: { ...arg }
      }),
      transformResponse: (response: { data: LoadLocation[]; total: number }) => ({
        locations: response.data,
        total: response.total
      }),
      providesTags: (result = { locations: [], total: 0 }, error, arg) => [
        'Location',
        ...result.locations.map(({ id }) => ({ type: 'Location' as const, id }))
      ]
    }),

    getLocationById: builder.query<LoadLocation, string>({
      query: (id) => {
        if (!id) throw new Error('Location id required');
        return {
          url: `/locations/${id}`,
          method: 'GET'
        };
      },
      transformResponse: (response: { data: LoadLocation }) => {
        return response.data;
      },
      providesTags: (result, error, arg) => [{ type: 'Location' as const, id: arg }]
    }),

    addLocation: builder.mutation<LoadLocation, CreateLocationData>({
      query: (arg) => ({
        url: '/locations',
        body: { ...arg },
        method: 'POST'
      }),
      transformResponse: (response: { data: LoadLocation }) => response.data,
      invalidatesTags: ['Location']
    }),

    updateLocation: builder.mutation<LoadLocation, UpdateLocationData>({
      query: (params) => {
        const { id, ...updateParams } = params;
        return {
          url: `/locations/${id}`,
          method: 'PUT',
          body: { ...updateParams }
        };
      },
      transformResponse: (response: { data: LoadLocation }) => response.data,
      invalidatesTags: (result, error, arg) => [{ type: 'Location', id: arg.id }]
    }),

    deleteLocation: builder.mutation<LoadLocation, string>({
      query: (id) => ({
        url: `/locations/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Location']
    })
  }),
  overrideExisting: false
});

export const useGetLocationsQuery = locationApi.endpoints.getLocations.useQuery;
export const useGetLocationByIdQuery = locationApi.endpoints.getLocationById.useQuery;
export const useAddLocationMutation = locationApi.endpoints.addLocation.useMutation;
export const useDeleteLocationMutation = locationApi.endpoints.deleteLocation.useMutation;
export const useUpdateLocationMutation = locationApi.endpoints.updateLocation.useMutation;
